import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Heart, CheckCircle } from 'lucide-react';
import { getOrder, submitTip } from '../services/api';

const tipOptions = [
  { amount: 300, label: '$3' },
  { amount: 500, label: '$5' },
  { amount: 1000, label: '$10' },
];

export default function TipDriver() {
  const { orderId } = useParams<{ orderId: string }>();
  const [selectedTip, setSelectedTip] = useState<number | null>(null);
  const [customTip, setCustomTip] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: !!orderId,
  });

  const tipMutation = useMutation({
    mutationFn: (amount: number) => submitTip(orderId!, amount),
    onSuccess: () => {
      setSubmitted(true);
    },
  });

  const handleSubmitTip = () => {
    const amount = selectedTip || (customTip ? Math.round(parseFloat(customTip) * 100) : 0);
    if (amount > 0) {
      tipMutation.mutate(amount);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-uber-green mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Thank You!</h1>
          <p className="text-gray-600">
            Your tip has been sent to your driver. They really appreciate it!
          </p>
        </div>
      </div>
    );
  }

  const latestDelivery = order?.deliveries?.[0];

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-6">
            <Heart className="w-12 h-12 text-red-500 mx-auto mb-2" />
            <h1 className="text-2xl font-bold">Tip Your Driver</h1>
            {latestDelivery?.driverName && (
              <p className="text-gray-600">
                {latestDelivery.driverName} delivered your package!
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            {tipOptions.map((option) => (
              <button
                key={option.amount}
                onClick={() => {
                  setSelectedTip(option.amount);
                  setCustomTip('');
                }}
                className={`py-4 rounded-lg font-bold text-lg transition-colors ${
                  selectedTip === option.amount
                    ? 'bg-uber-green text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <div className="mb-6">
            <label className="block text-sm text-gray-600 mb-2">
              Or enter a custom amount:
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">
                $
              </span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={customTip}
                onChange={(e) => {
                  setCustomTip(e.target.value);
                  setSelectedTip(null);
                }}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uber-green focus:border-transparent"
              />
            </div>
          </div>

          <button
            onClick={handleSubmitTip}
            disabled={tipMutation.isPending || (!selectedTip && !customTip)}
            className="btn-primary w-full py-3 text-lg"
          >
            {tipMutation.isPending
              ? 'Processing...'
              : `Send Tip${
                  selectedTip
                    ? ` (${tipOptions.find((o) => o.amount === selectedTip)?.label})`
                    : customTip
                    ? ` ($${customTip})`
                    : ''
                }`}
          </button>

          <button
            onClick={() => window.close()}
            className="w-full text-center text-gray-500 hover:text-gray-700 mt-4"
          >
            No thanks, maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
